/**
 * AWS API Gateway client with JWT authentication
 * Calls AWS API Gateway directly instead of Next.js proxy routes
 */

import { getSession, signOut } from 'next-auth/react';
import { PartialFailureError, AllFailedError, InsufficientCreditsError } from './errors';
import type {
  VieVideoAnalysis,
  VieTechniqueLibrary,
  VieDashboardMetrics,
  VieReplyDraft,
  VieReplyApproval,
  VieRecommendations,
} from '@/types/vie';

const AWS_API_BASE_URL = process.env.NEXT_PUBLIC_AWS_API_URL;
const INTERVIEW_API_BASE_URL = process.env.NEXT_PUBLIC_INTERVIEW_API_URL;
// AgentCore endpoint for SSE-streamed interview messages (Issue #2084)
export const AGENTCORE_ENDPOINT_URL = process.env.NEXT_PUBLIC_AGENTCORE_ENDPOINT_URL;

/**
 * AWS API client that handles JWT authentication
 */
class AwsApiClient {
  private baseUrl: string | undefined;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url_base = this.baseUrl || AWS_API_BASE_URL;
    if (!url_base) {
      throw new Error(`API base URL not configured (${this.baseUrl ? 'custom' : 'NEXT_PUBLIC_AWS_API_URL'})`);
    }

    // Get the session from NextAuth (works on client side)
    const session = await getSession() as any;

    if (!session?.user?.email) {
      throw new Error('Not authenticated - please sign in');
    }

    if (session.error === 'RefreshAccessTokenError') {
      signOut({ callbackUrl: '/auth/signin' });
      throw new Error('Authentication expired');
    }

    // Get the Google ID token from the session
    const idToken = session.idToken;

    // console.log('🔍 [AWS API] Token Debug:', {
    //   endpoint,
    //   method: options.method || 'GET',
    //   hasIdToken: !!idToken,
    //   tokenExpiry: session.expires,
    //   tokenLength: idToken ? idToken.length : 0,
    //   sessionError: session.error,
    //   hasOptionsHeaders: !!options.headers
    // });

    if (!idToken) {
      console.error('❌ [AWS API] No ID token in session. Full session:', session);
      throw new Error('No authentication token available - please sign out and sign in again');
    }


    const url = `${url_base}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
      ...options.headers,
    };

    // console.log('📤 [AWS API] Making request:', {
    //   url,
    //   method: options.method || 'GET',
    //   hasAuthHeader: !!headers.Authorization,
    //   authHeaderPreview: headers.Authorization ? headers.Authorization.substring(0, 20) + '...' : 'MISSING',
    //   allHeaders: Object.keys(headers)
    // });

    const response = await fetch(url, {
      headers,
      ...options,
    });

    // console.log('📥 [AWS API] Response received:', {
    //   endpoint,
    //   status: response.status,
    //   statusText: response.statusText,
    //   ok: response.ok
    // });

    // Only treat 401 as an auth error that warrants token refresh + signOut.
    // 403 can mean "access denied to resource" (not an auth issue), so handle separately.
    if (response.status === 401) {
      console.error('⚠️ [AWS API] Auth error (401), attempting token refresh', { endpoint });

      const newSession = await getSession() as any;

      if (newSession?.error === 'RefreshAccessTokenError' || !newSession?.idToken) {
        signOut({ callbackUrl: '/auth/signin' });
        throw new Error('Authentication expired');
      }

      const retryResponse = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${newSession.idToken}`,
          ...options.headers,
        },
        ...options,
      });

      if (retryResponse.status === 401) {
        signOut({ callbackUrl: '/auth/signin' });
        throw new Error('Authentication expired');
      }

      if (retryResponse.status === 403) {
        const errorData = await retryResponse.json().catch(() => ({ error: 'Forbidden' }));
        throw new Error(errorData.error || 'Access denied');
      }

      if (!retryResponse.ok) {
        const errorData = await retryResponse.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `AWS API error: ${retryResponse.status}`);
      }

      // console.log('✅ [AWS API] Token refresh and retry successful!');

      // Parse retry response and check for partial failures
      const retryData = await retryResponse.json();

      if (retryData.failed_ids && Array.isArray(retryData.failed_ids) && retryData.failed_ids.length > 0) {
        const successCount = retryData.approved_count || retryData.rejected_count || retryData.scheduled_count || 0;

        if (successCount === 0) {
          throw new AllFailedError(retryData.failed_ids);
        }

        throw new PartialFailureError(
          retryData.failed_ids,
          successCount,
          retryData.errors || [],
          retryData.requires_schedule || false,
          retryData.requires_channel || false
        );
      }

      return retryData;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

      // Handle insufficient credits (402 Payment Required)
      if (response.status === 402 && errorData.error === 'insufficient_credits') {
        throw new InsufficientCreditsError(
          errorData.cost || 0,
          errorData.available || 0,
          errorData.message
        );
      }

      throw new Error(errorData.message || errorData.error || `AWS API error: ${response.status}`);
    }

    // Parse the response body
    const responseData = await response.json();

    // Check for partial failures in successful responses (Issue #18)
    // Backend may return 200 OK with failed_ids when some operations fail
    if (responseData.failed_ids && Array.isArray(responseData.failed_ids) && responseData.failed_ids.length > 0) {
      const successCount = responseData.approved_count || responseData.rejected_count || responseData.scheduled_count || 0;

      // If ALL operations failed, throw error with failed IDs
      if (successCount === 0) {
        throw new AllFailedError(responseData.failed_ids);
      }

      // If SOME operations failed, throw error with failed IDs, counts, and detailed error info
      throw new PartialFailureError(
        responseData.failed_ids,
        successCount,
        responseData.errors || [],
        responseData.requires_schedule || false,
        responseData.requires_channel || false
      );
    }

    return responseData;
  }

  // GET request
  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // POST request
  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT request
  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PATCH request
  async patch<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Export singleton instance
export const awsApi = new AwsApiClient();

// Specific API methods for streams calling AWS API Gateway directly
export const awsStreamsApi = {
  // GET /streams
  list: () => awsApi.get('/streams'),

  // GET /streams/[id]
  get: (id: string) => awsApi.get(`/streams/${id}`),

  // POST /streams
  create: (data: any) => awsApi.post('/streams', data),

  // PUT /streams/[id]
  update: (id: string, data: any) => awsApi.put(`/streams/${id}`, data),

  // DELETE /streams/[id]
  delete: (id: string) => awsApi.delete(`/streams/${id}`),

  // POST /streams/samples - Create sample streams for new user (first-time setup)
  createSamples: (): Promise<{ message: string; created: boolean }> =>
    awsApi.post('/streams/samples'),

  // GET /streams/{stream_id}/defaults-preview - Preview which stories would be affected by defaults
  previewDefaults: (streamId: string): Promise<{
    eligible_count: number;
    skipped_has_video: number;
    skipped_custom: number;
    eligible_stories: Array<{ story_id: string; name: string }>;
  }> => awsApi.get(`/streams/${streamId}/defaults-preview`),

  // POST /streams/{stream_id}/apply-defaults - Apply stream defaults to existing stories
  applyDefaults: (streamId: string, data: {
    apply_to?: 'eligible' | 'all';
    settings?: string[];
  }): Promise<{
    updated_count: number;
    skipped_has_video: number;
    skipped_custom: number;
    message: string;
  }> => awsApi.post(`/streams/${streamId}/apply-defaults`, data),
};

// Quote generation types
export type LLMProvider = 'bedrock' | 'gemini';

export interface LLMModelOption {
  id: string;       // SSM alias (flash, gemini, opus) - backend resolves to provider:model_id
  name: string;
  description: string;
  costIndicator: '$' | '$$' | '$$$';
  costMultiplier: number;  // Credit cost multiplier (1.0, 3.0, or 5.0)
}

// CRITICAL: Load cost multipliers from shared config to ensure frontend/backend sync
// Source of truth: serverless-video-generator/shared/model-pricing.ts
// Run `npm test -- pricing-sync.test.ts` to verify sync
import { MODEL_PRICING } from './shared/model-pricing';

const getMultiplier = (modelId: string): number => {
  return MODEL_PRICING.multipliers[modelId as keyof typeof MODEL_PRICING.multipliers] || 1.0;
};

export const LLM_MODEL_OPTIONS: LLMModelOption[] = [
  {
    id: 'flash',
    name: 'Gemini Flash',
    description: 'Fast and efficient',
    costIndicator: '$',
    costMultiplier: getMultiplier('flash'),  // 1.0x
  },
  {
    id: 'gemini',
    name: 'Gemini Pro',
    description: 'Highest quality Gemini',
    costIndicator: '$$',
    costMultiplier: getMultiplier('gemini'),  // 3.0x
  },
  {
    id: 'opus',
    name: 'Claude Opus',
    description: 'Most capable model',
    costIndicator: '$$$',
    costMultiplier: getMultiplier('opus'),  // 5.0x
  },
];

export interface GenerateQuotesRequest {
  stream_id: string;
  count: number;
  words_per_quote?: number;  // 50-300, controls AI output length (default: 100)
  template_id?: string;
  custom_prompt?: string;
  structured?: boolean;  // When true, adds engaging video structure (hook, content, insight, takeaway)
  model?: string;  // SSM alias: 'flash', 'gemini', or 'opus' (backend resolves to provider:model_id)
  series_id?: string;  // Optional series ID for batch generation from a series
}

export interface GenerateQuotesResponse {
  job_id: string;
  batch_id: string;
  status: string;
  count: number;
  words_per_quote: number;
  total_words: number;
  credits_used: number;
  message: string;
}

// Quotes API
export const awsQuotesApi = {
  list: (params?: { stream_id?: string; status?: string; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.stream_id) queryParams.append('stream_id', params.stream_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return awsApi.get(`/quotes${query}`);
  },
  approve: (data: any) => awsApi.post('/quotes/approve', data),
  reject: (data: any) => awsApi.post('/quotes/reject', data),
  bulkApprove: (data: any) => awsApi.post('/quotes/bulk-approve', data),
  generate: (data: GenerateQuotesRequest): Promise<GenerateQuotesResponse> =>
    awsApi.post('/quotes/generate', data),
  pending: () => awsApi.get('/quotes/pending'),
  updateVoice: (quoteId: string, data: any) => awsApi.put(`/quotes/${quoteId}/voice`, data),
  delete: (quoteId: string) => awsApi.delete(`/quotes/${quoteId}`),
  update: (quoteId: string, data: any) => awsApi.put(`/quotes/${quoteId}`, data),
  create: (data: { quote_text: string; source_text?: string; stream_id: string }) =>
    awsApi.post('/quotes', data),
};

// Credits API
export const awsCreditsApi = {
  get: () => awsApi.get('/credits'),
};

// User Profile API
export const awsUserApi = {
  getProfile: (email: string) => awsApi.get(`/users/${encodeURIComponent(email)}`),
};

// Images API
export const awsImagesApi = {
  list: (streamId?: string) => {
    const queryParams = streamId ? `?stream_id=${encodeURIComponent(streamId)}` : '';
    return awsApi.get(`/images${queryParams}`);
  },
  upload: (data: any) => awsApi.post('/images/upload', data),
  delete: (imageId: string) => awsApi.delete(`/images/${imageId}`),
  
  // Assignment operations - all go to /images/assign endpoint with different actions
  assignBatch: (data: any) => awsApi.post('/images/assign', { action: 'assign_batch', ...data }),
  assignGradients: (data: any) => awsApi.post('/images/assign', { action: 'assign_gradients', ...data }),
  assignCaptions: (data: any) => awsApi.post('/images/assign', { action: 'assign_captions', ...data }),
  assignBackgroundMusic: (data: any) => awsApi.post('/images/assign', { action: 'assign_background_music', ...data }),
  removeBackgroundMusic: (data: any) => awsApi.post('/images/assign', { action: 'remove_background_music', ...data }),
  removeBackground: (data: any) => awsApi.post('/images/assign', { action: 'remove_background', ...data }),
  autoAssign: (data: any) => awsApi.post('/images/assign', { action: 'assign_batch', ...data }),
};

// Audio API
export const awsAudioApi = {
  generate: (data: any) => awsApi.post('/audio/generate', data),
  upload: (data: any) => awsApi.post('/audio/upload', data),
  preview: (data: any) => awsApi.post('/audio/preview', data),
  // Presigned URL upload for large audio files (bypasses API Gateway size limits)
  getUploadUrl: (data: { filename: string; content_type: string; stream_id: string; file_size?: number }) =>
    awsApi.post('/audio/upload', { action: 'get_upload_url', ...data }),
  confirmUpload: (data: { audio_id: string; s3_key: string; stream_id: string; filename: string; content_type: string; file_size?: number }) =>
    awsApi.post('/audio/upload', { action: 'confirm_upload', ...data }),
};

// Job status filter type
export type JobStatusFilter = 'active' | 'completed' | 'failed' | 'all';

// Videos API
export const awsVideosApi = {
  generate: (data: any) => awsApi.post('/videos/generate', data),
  process: (data: any) => awsApi.post('/videos/process', data),
  upload: (data: any) => awsApi.post('/videos/upload', data),
  // Presigned URL upload for large videos (bypasses API Gateway size limits)
  getUploadUrl: (data: { filename: string; content_type: string; stream_id: string; file_size?: number }) =>
    awsApi.post('/videos/upload', { action: 'get_upload_url', ...data }),
  confirmUpload: (data: { video_id: string; s3_key: string; stream_id: string; filename: string; content_type: string; file_size?: number }) =>
    awsApi.post('/videos/upload', { action: 'confirm_upload', ...data }),
  batch: (data: any) => awsApi.post('/videos/batch', data),
  /**
   * Get jobs with optional status filter
   * @param status - 'active' (default), 'completed', 'failed', or 'all'
   */
  getJobs: (status?: JobStatusFilter) => {
    const query = status ? `?status=${status}` : '';
    return awsApi.get(`/videos/batch${query}`);
  },
  getDetailedJobs: () => awsApi.get('/videos/batch?detailed=true'),
  cancelJob: (batchId: string) => awsApi.delete(`/videos/batch/${batchId}`),
};

// Voices API
export const awsVoicesApi = {
  /**
   * List all voices (legacy - loads all voices)
   * For better performance, use listLanguages() + listByLanguage()
   */
  list: (includeLanguages = true) => {
    const queryParams = includeLanguages ? '?include_languages=true' : '';
    return awsApi.get(`/voices${queryParams}`);
  },

  /**
   * List available languages with voice counts (fast)
   * Returns: { languages: [{code, name, display, voice_count}], total_languages }
   */
  listLanguages: () => awsApi.get('/voices/languages'),

  /**
   * List voices for a specific language (fast - uses GSI)
   * @param language - Language code (e.g., "ta-IN", "en-US")
   */
  listByLanguage: (language: string) =>
    awsApi.get(`/voices?language=${encodeURIComponent(language)}`),
};

// Metadata API
export const awsMetadataApi = {
  generate: (data: any) => awsApi.post('/metadata/generate', data),
};

// Engagement API - Comment Intelligence (Issue #1326)
export const awsEngagementApi = {
  // GET /engagement/comments - List comments with filters
  listComments: (params: {
    channel_id: string;
    sentiment?: string;
    video_id?: string;
    is_read?: boolean;
    is_starred?: boolean;
    page_token?: string;
    limit?: number;
  }) => {
    const qp = new URLSearchParams();
    qp.append('channel_id', params.channel_id);
    if (params.sentiment) qp.append('sentiment', params.sentiment);
    if (params.video_id) qp.append('video_id', params.video_id);
    if (params.is_read !== undefined) qp.append('is_read', String(params.is_read));
    if (params.is_starred !== undefined) qp.append('is_starred', String(params.is_starred));
    if (params.page_token) qp.append('page_token', params.page_token);
    if (params.limit) qp.append('limit', String(params.limit));
    return awsApi.get(`/engagement/comments?${qp.toString()}`);
  },

  // PATCH /engagement/comments/{comment_id} - Update comment state
  updateComment: (commentId: string, data: {
    video_id: string;
    is_read?: boolean;
    is_starred?: boolean;
  }) => awsApi.patch(`/engagement/comments/${encodeURIComponent(commentId)}`, data),

  // POST /engagement/comments/mark-read - Batch mark as read
  markRead: (channelId: string, items: Array<{ video_id: string; comment_id: string }>) =>
    awsApi.post('/engagement/comments/mark-read', { channel_id: channelId, items }),

  // GET /engagement/stats - Get engagement statistics
  getStats: (channelId: string) =>
    awsApi.get(`/engagement/stats?channel_id=${encodeURIComponent(channelId)}`),

  // POST /engagement/refresh-comments - Trigger on-demand comment polling
  refreshComments: () => awsApi.post('/engagement/refresh-comments', {}),

  // POST /engagement/comments/{comment_id}/suggest-reply - Get AI reply suggestions (Phase 3a)
  suggestReply: (commentId: string, data: { video_id: string; channel_id: string }) =>
    awsApi.post(`/engagement/comments/${encodeURIComponent(commentId)}/suggest-reply`, data),

  // POST /engagement/comments/{comment_id}/reply - Post reply to YouTube (Phase 3a)
  postReply: (commentId: string, data: {
    video_id: string;
    channel_id: string;
    reply_text: string;
    reply_source: 'ai_suggested' | 'ai_edited' | 'manual';
  }) => awsApi.post(`/engagement/comments/${encodeURIComponent(commentId)}/reply`, data),
};

// YouTube API - JWT provides user identity (no user_id in query params)
export const awsYouTubeApi = {
  // Channel management - stream_id only, user from JWT
  listChannels: (params?: { stream_id?: string }) => {
    if (params?.stream_id) {
      return awsApi.get(`/youtube/channels?stream_id=${encodeURIComponent(params.stream_id)}`);
    }
    return awsApi.get('/youtube/channels');
  },
  // List ALL user's channels across all streams (deduplicated)
  listAllChannels: () => awsApi.get('/youtube/channels'),
  updateChannel: (data: any) => awsApi.post('/youtube/channels', data),
  disconnectChannel: (data: any) => awsApi.post('/youtube/channels', { ...data, action: 'disconnect' }),
  setDefaultChannel: (data: any) => awsApi.post('/youtube/channels', { ...data, action: 'set_default' }),
  // Link an existing channel to a new stream (reuses tokens, no new OAuth)
  linkExistingChannel: (data: { source_channel_id: string; target_stream_id: string }) =>
    awsApi.post('/youtube/channels', { ...data, action: 'link_existing_channel' }),

  // OAuth flow - stream_id only, user from JWT
  // return_url: where to redirect after OAuth completes
  initiateOAuth: (params?: { stream_id?: string; return_url?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.stream_id) {
      queryParams.set('stream_id', params.stream_id);
    }
    if (params?.return_url) {
      queryParams.set('return_url', params.return_url);
    }
    const queryString = queryParams.toString();
    return awsApi.get(`/youtube/oauth${queryString ? `?${queryString}` : ''}`);
  },

  // Video publishing
  publishVideo: (data: any) => awsApi.post('/youtube/publish', data),

  // Scheduling
  scheduleVideo: (data: any) => awsApi.post('/youtube/schedule', data),

  // Engagement Dashboard (Phase 2a)
  getEngagementDashboard: (channelId: string, days: number) =>
    awsApi.get(`/youtube/analytics/engagement-dashboard?channel_id=${encodeURIComponent(channelId)}&days=${days}`),
};

// VIE (Viral Intelligence Engine) API — Issue #1807
export const awsVieApi = {
  // POST /vie/analyze — analyze a YouTube video, extract techniques
  analyzeVideo: (data: { video_id?: string; youtube_url?: string }) =>
    awsApi.post<VieVideoAnalysis>('/vie/analyze', data),

  // GET /vie/techniques — list all extracted techniques, sorted by virality_score
  getTechniques: (params?: { limit?: number; category?: string }) => {
    const qp = new URLSearchParams();
    if (params?.limit !== undefined) qp.append('limit', String(params.limit));
    if (params?.category) qp.append('category', params.category);
    const qs = qp.toString();
    return awsApi.get<VieTechniqueLibrary>(`/vie/techniques${qs ? `?${qs}` : ''}`);
  },

  // GET /vie/metrics/dashboard — 30-day engagement dashboard
  getDashboard: () =>
    awsApi.get<VieDashboardMetrics>('/vie/metrics/dashboard'),

  // POST /vie/reply/generate — generate an AI reply draft for a comment
  generateReply: (data: {
    comment_id: string;
    video_id: string;
    channel_tone_warmth?: number;
    channel_tone_formality?: number;
  }) =>
    awsApi.post<VieReplyDraft>('/vie/reply/generate', data),

  // POST /vie/reply/approve — approve and post a reply to YouTube
  approveReply: (data: {
    comment_id: string;
    video_id: string;
    channel_id: string;
    user_id: string;
    reply_text?: string;
  }) =>
    awsApi.post<VieReplyApproval>('/vie/reply/approve', data),

  // GET /vie/optimize — AI-generated content recommendations
  getRecommendations: () =>
    awsApi.get<VieRecommendations>('/vie/optimize'),
};

/**
 * TikTok API - Using real TikTok Sandbox API
 *
 * Uses TikTok OAuth 2.0 with Sandbox credentials for testing.
 * Backend implementation will be added in Issue #900.
 *
 * TikTok-specific considerations:
 * - No scheduled publishing (TikTok doesn't support it)
 * - Privacy options: public, friends, private
 * - Duet/Stitch settings
 * - Content disclosure requirements
 */

// TikTok Sandbox credentials (can be overridden by environment variables)
const TIKTOK_CLIENT_KEY = process.env.NEXT_PUBLIC_TIKTOK_CLIENT_KEY || 'sbaw0hdiaao9io4h1i';
const TIKTOK_REDIRECT_URI = typeof window !== 'undefined'
  ? `${window.location.origin}/api/auth/tiktok/callback`
  : 'https://kreayt.ai/api/auth/tiktok/callback';

export const awsTikTokApi = {
  // Account management - stream_id only, user from JWT
  listAccounts: (params?: { stream_id?: string }): Promise<{ accounts: any[] }> => {
    // Check for account data in cookie (set by OAuth callback)
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';');
      const tiktokCookie = cookies.find(c => c.trim().startsWith('tiktok_account='));
      if (tiktokCookie) {
        try {
          const accountData = JSON.parse(decodeURIComponent(tiktokCookie.split('=')[1]));
          // Filter by stream_id if provided
          if (!params?.stream_id || accountData.stream_id === params.stream_id) {
            console.log('[TikTok API] Found account in cookie:', accountData.username);
            return Promise.resolve({ accounts: [accountData] });
          }
        } catch (e) {
          console.error('[TikTok API] Error parsing cookie:', e);
        }
      }
    }
    console.log('[TikTok API] No accounts found');
    return Promise.resolve({ accounts: [] });
  },

  // OAuth flow - initiates TikTok authorization using real Sandbox API
  initiateOAuth: (params: { stream_id: string; return_url?: string }): Promise<{ auth_url: string }> => {
    console.log('[TikTok API] Initiating OAuth with Sandbox credentials');

    // Build state parameter with CSRF token and return info
    const state = encodeURIComponent(JSON.stringify({
      stream_id: params.stream_id,
      return_url: params.return_url || '/',
      csrf: Math.random().toString(36).substring(2, 15),
    }));

    // Build the real TikTok OAuth authorization URL
    const authUrl = new URL('https://www.tiktok.com/v2/auth/authorize/');
    authUrl.searchParams.set('client_key', TIKTOK_CLIENT_KEY);
    authUrl.searchParams.set('scope', 'user.info.basic,video.publish,video.upload');
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('redirect_uri', TIKTOK_REDIRECT_URI);
    authUrl.searchParams.set('state', state);

    console.log('[TikTok API] OAuth URL:', authUrl.toString());

    return Promise.resolve({ auth_url: authUrl.toString() });
  },

  // Disconnect account from stream - clears the cookie
  disconnectAccount: (params: { account_id: string; stream_id: string }): Promise<{ success: boolean }> => {
    console.log('[TikTok API] Disconnecting account:', params);
    // Clear the tiktok_account cookie
    if (typeof document !== 'undefined') {
      document.cookie = 'tiktok_account=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
    return Promise.resolve({ success: true });
  },

  // Publish video to TikTok (direct publish, no scheduling)
  publish: (params: {
    video_url: string;
    stream_id: string;
    account_id: string;
    title?: string;
    privacy_level?: 'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'SELF_ONLY';
    disable_duet?: boolean;
    disable_stitch?: boolean;
    disable_comment?: boolean;
    brand_content_toggle?: boolean;
    brand_organic_toggle?: boolean;
  }): Promise<{ tiktok_video_id: string; tiktok_video_url: string }> => {
    console.log('[TikTok Scaffolding] publish called with:', params);
    return Promise.resolve({
      tiktok_video_id: 'demo_video_' + Date.now(),
      tiktok_video_url: 'https://www.tiktok.com/@demo_creator/video/demo_' + Date.now()
    });
  },
};

// Schedule Pattern API - RESTful (GET/PUT/DELETE) - per stream
export const awsSchedulePatternApi = {
  get: (streamId: string) => awsApi.get(`/schedule-pattern?stream_id=${encodeURIComponent(streamId)}`),
  save: (streamId: string, pattern: any) => awsApi.put('/schedule-pattern', { stream_id: streamId, pattern }),
  delete: (streamId: string) => awsApi.delete(`/schedule-pattern?stream_id=${encodeURIComponent(streamId)}`),
};

// Stories API
export const awsStoriesApi = {
  // POST /stories/generate-content - Generate AI story content
  generateContent: (data: any) => awsApi.post('/stories/generate-content', data),

  // POST /stories - Create new story
  create: (data: any) => awsApi.post('/stories', data),

  // GET /stories - List stories
  list: (params?: { stream_id?: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.stream_id) queryParams.append('stream_id', params.stream_id);
    if (params?.status) queryParams.append('status', params.status);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return awsApi.get(`/stories${query}`);
  },

  // GET /stories/{story_id} - Get story details
  get: (storyId: string) => awsApi.get(`/stories/${storyId}`),

  // PUT /stories/{story_id} - Update story-level settings (voice, captions, music, metadata)
  update: (storyId: string, data: any) => awsApi.put(`/stories/${storyId}`, data),

  // PUT /stories/{story_id}/scenes/{scene_id} - Update scene
  updateScene: (storyId: string, sceneId: string, data: any) =>
    awsApi.put(`/stories/${storyId}/scenes/${sceneId}`, data),

  // PUT /stories/{story_id}/scenes/reorder - Reorder scenes
  reorderScenes: (storyId: string, data: any) =>
    awsApi.put(`/stories/${storyId}/scenes/reorder`, data),

  // DELETE /stories/{story_id}/scenes/{scene_id} - Delete scene
  deleteScene: (storyId: string, sceneId: string) =>
    awsApi.delete(`/stories/${storyId}/scenes/${sceneId}`),

  // POST /stories/{story_id}/scenes - Create new scene
  createScene: (storyId: string, data: { order?: number; title?: string; text?: string; status?: string }) =>
    awsApi.post(`/stories/${storyId}/scenes`, data),

  // POST /stories/{story_id}/generate-audio - Start audio generation
  generateAudio: (storyId: string) => awsApi.post(`/stories/${storyId}/generate-audio`, {}),

  // GET /stories/{story_id}/audio-status - Get audio generation status
  getAudioStatus: (storyId: string) => awsApi.get(`/stories/${storyId}/audio-status`),

  // Poll for audio generation completion
  pollAudioUntilComplete: async (
    storyId: string,
    options: { maxAttempts?: number; intervalMs?: number; onStatusChange?: (status: any) => void } = {}
  ) => {
    const { maxAttempts = 60, intervalMs = 2000, onStatusChange } = options;
    let lastStatus: string | undefined;

    for (let i = 0; i < maxAttempts; i++) {
      const status = await awsStoriesApi.getAudioStatus(storyId);

      if (onStatusChange && status.status !== lastStatus) {
        onStatusChange(status);
        lastStatus = status.status;
      }

      if (status.status === 'audio_ready' || status.status === 'completed') {
        return status;
      }

      if (status.status === 'draft' && status.error) {
        throw new Error(status.error);
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }

    throw new Error(`Audio generation timeout: did not complete within ${maxAttempts * intervalMs / 1000} seconds`);
  },

  // GET /stories/{story_id}/analyze-media - Analyze media aspect ratios before video generation
  analyzeMedia: (storyId: string): Promise<{
    has_media: boolean;
    recommended_ratio: string;
    is_mixed: boolean;
    portrait_count: number;
    landscape_count: number;
    square_count: number;
    total_analyzed: number;
    probed_items?: Array<{ scene_id: string; type: string; width: number; height: number; orientation: string }>;
    message: string;
  }> => awsApi.get(`/stories/${storyId}/analyze-media`),

  // POST /stories/{story_id}/generate-video - Start video generation (Issue #685)
  // Accepts optional aspect_ratio to override auto-detection
  generateVideo: (storyId: string, options?: { aspect_ratio?: '9:16' | '16:9' | '1:1' }) =>
    awsApi.post(`/stories/${storyId}/generate-video`, options || {}),

  // GET /stories/{story_id}/video-status - Get video generation status (Issue #685)
  getVideoStatus: (storyId: string) => awsApi.get(`/stories/${storyId}/video-status`),

  // POST /streams/{stream_id}/detect-stories - Detect stories from prompt (Issue #929)
  detectStories: (streamId: string, data: {
    prompt: string;
    target_duration_minutes?: number;
  }): Promise<{
    detected_count: number;
    stories_preview: string[];
    all_stories: string[];
    estimated_credits: number;
    user_credits: number;
    target_duration_minutes: number;
    batch_limit: number;
    stream_defaults: {
      voice_id?: string;
      aspect_ratio?: string;
      caption_style?: any;
    };
  }> => awsApi.post(`/streams/${streamId}/detect-stories`, data),

  // POST /streams/{stream_id}/generate-stories - Batch generate stories (Issue #929)
  generateBatch: (streamId: string, data: {
    prompt: string;
    custom_prompt?: string; // Additional instructions for story generation
    stories?: string[];
    count?: number; // Number of stories to generate (uses stream's AI prompt)
    target_duration_minutes?: number;
    aspect_ratio?: '16:9' | '9:16' | '1:1';
    provider?: LLMProvider; // LLM provider
    model?: string; // LLM model ID
    // Stream defaults for voice and captions (inherited from stream.defaults)
    voice_id?: string;
    voice_speed?: number;
    tts_provider?: 'aws_polly' | 'google_tts' | 'gemini_tts';
    caption_style?: {
      font_family?: string;
      font_size?: number;
      font_color?: string;
      background_color?: string;
      position?: string;
      alignment?: string;
      sync_enabled?: boolean;
      emphasis_color?: string;
    };
  }): Promise<{
    batch_id: string;
    stories_queued: number;
    status: string;
    credits_charged: number;
  }> => awsApi.post(`/streams/${streamId}/generate-stories`, data),

  // GET /streams/{stream_id}/batch-status/{batch_id} - Get batch generation status (Issue #929)
  getBatchStatus: (streamId: string, batchId: string): Promise<{
    batch_id: string;
    status: string;
    total_stories: number;
    completed_count: number;
    failed_count: number;
    pending_count: number;
    progress_percent: number;
    created_at: string;
    updated_at: string;
  }> => awsApi.get(`/streams/${streamId}/batch-status/${batchId}`),

  // Poll for video generation completion (Issue #685)
  pollVideoUntilComplete: async (
    storyId: string,
    options: { maxAttempts?: number; intervalMs?: number; onStatusChange?: (status: any) => void } = {}
  ) => {
    const { maxAttempts = 180, intervalMs = 3000, onStatusChange } = options; // 9 minutes max
    let lastStatus: string | undefined;

    for (let i = 0; i < maxAttempts; i++) {
      const status = await awsStoriesApi.getVideoStatus(storyId);

      if (onStatusChange && status.status !== lastStatus) {
        onStatusChange(status);
        lastStatus = status.status;
      }

      if (status.status === 'completed' || status.status === 'ready') {
        return status;
      }

      if (status.status === 'failed' || (status.status === 'audio_ready' && status.error)) {
        throw new Error(status.error || 'Video generation failed');
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }

    throw new Error(`Video generation timeout: did not complete within ${maxAttempts * intervalMs / 1000} seconds`);
  },
};

// NOTE: Series API removed - Series functionality deprecated
// Batch story generation can be re-implemented at Stream level later

// Scheduled Publishes API
export const awsPublishesApi = {
  listScheduled: (params?: any) => {
    if (params?.stream_id) {
      const query = new URLSearchParams(params).toString();
      return awsApi.get(`/publishes/scheduled?${query}`);
    }
    return awsApi.get('/publishes/scheduled');
  },
  createSchedule: (data: any) => awsApi.post('/publishes/scheduled', data),
  deleteSchedule: (scheduleId: string) => awsApi.delete(`/publishes/scheduled?schedule_id=${scheduleId}`),
};

// Stripe Checkout API
export const awsCheckoutApi = {
  create: (data: any) => awsApi.post('/stripe/create-checkout', data),
  createPortal: (data: any) => awsApi.post('/stripe/create-portal', data),
};

// Prompt Templates API (Admin Only)
export const awsPromptTemplatesApi = {
  /** List all prompt templates */
  list: () => awsApi.get('/prompt-templates'),
  /** Create a new version of a prompt template */
  createVersion: (data: {
    agent_name: string;
    prompt_content: string;
    description?: string;
    language?: string;
    status?: string;
    generation_params?: { temperature?: number; max_tokens?: number; model?: string };
    config_data?: Record<string, unknown>;
  }) => awsApi.post('/prompt-templates', data),
  /** Update status of a prompt template (ACTIVE/DRAFT/ARCHIVED). Setting ACTIVE auto-archives the previous active version. */
  updateStatus: (data: { template_id: string; status: 'ACTIVE' | 'DRAFT' | 'ARCHIVED' }) =>
    awsApi.patch('/prompt-templates', data),
  /** Get variables extracted from a prompt template with genre-resolved values */
  getVariables: (agentName: string) =>
    awsApi.get(`/prompt-templates/variables?agent_name=${encodeURIComponent(agentName)}`),
  /** Preview composed scaffold + language profile */
  composePreview: (data: { scaffold_name: string; profile_name: string; genre?: string }) =>
    awsApi.post('/prompt-templates/compose', data),
  /** Submit an async test job for a prompt template. Returns {jobId, status}. */
  submitTest: (data: {
    agent_name: string;
    version?: number;
    variables?: Record<string, string>;
    genre?: string;
    llm_config_override?: { provider?: string; model?: string; temperature?: number; max_tokens?: number };
  }): Promise<{ jobId: string; status: string }> => awsApi.post('/prompt-templates/test', data),

  /** Poll for async test job result */
  pollTestResult: (jobId: string): Promise<{
    jobId: string;
    status: 'PENDING' | 'COMPLETED' | 'FAILED';
    rendered_prompt?: string;
    llm_response?: string;
    token_usage?: { input_tokens: number; output_tokens: number; total_tokens: number };
    latency_ms?: number;
    model_used?: string;
    template_id?: string;
    stop_reason?: string;
    error?: string;
  }> => awsApi.get(`/prompt-templates/test?jobId=${encodeURIComponent(jobId)}`),
  /** Import run inputs (S3 artifacts) as test variables for a given agent */
  getRunInputs: (runId: string, agentName: string): Promise<{
    variables: Record<string, string>;
    genre?: string;
    runId: string;
    available_files: string[];
  }> =>
    awsApi.get(
      `/prompt-templates/run-inputs?runId=${encodeURIComponent(runId)}&agent_name=${encodeURIComponent(agentName)}`
    ),
};

// Prompt Eval API (Admin Only) — Issue #1813
export const awsPromptEvalApi = {
  // --------------- Suites ---------------

  /** List all test suites, optionally filtered by agent_name */
  listSuites: (agentName?: string): Promise<{ suites: Array<{
    suite_id: string;
    name: string;
    description?: string;
    input_count: number;
    created_at: string;
    updated_at?: string;
    source_run_ids?: string[];
  }> }> => {
    const qp = new URLSearchParams();
    if (agentName) qp.append('agent_name', agentName);
    const qs = qp.toString();
    return awsApi.get(`/prompt-eval/suites${qs ? `?${qs}` : ''}`);
  },

  /** Get a test suite and its inputs */
  getSuite: (suiteId: string): Promise<{
    suite_id: string;
    name: string;
    description?: string;
    input_count: number;
    created_at: string;
    updated_at?: string;
    source_run_ids?: string[];
    inputs: Array<{
      suite_id: string;
      input_idx: number;
      variables: Record<string, string>;
      genre?: string;
      label?: string;
      source_run_id?: string;
    }>;
  }> =>
    awsApi.get(`/prompt-eval/suites?suiteId=${encodeURIComponent(suiteId)}`),

  /**
   * Create a test suite by importing JSON outputs from one or more pipeline runs.
   * POST /prompt-eval/suites/from-runs
   */
  createSuiteFromRuns: (data: {
    name: string;
    description?: string;
    agent_name?: string;
    run_ids: string[];
    extra_variables?: Record<string, string>;
  }): Promise<{
    suite_id: string;
    name: string;
    input_count: number;
    imported_runs?: number;
    skipped_runs: string[];
    created_at: string;
  }> =>
    awsApi.post('/prompt-eval/suites/from-runs', data),

  // --------------- Batches ---------------

  /**
   * Create a batch eval run against a test suite.
   * POST /prompt-eval/batch
   */
  createBatch: (data: {
    agent_name: string;
    version?: number;
    suite_id: string;
    genre?: string;
    config?: {
      concurrency?: number;
      [key: string]: unknown;
    };
  }): Promise<{
    batch_id: string;
    status: string;
    total_tests: number;
    cost_estimate_usd: number;
  }> =>
    awsApi.post('/prompt-eval/batch', data),

  /** Get status and progress for a specific batch */
  getBatchStatus: (batchId: string): Promise<{
    batch_id: string;
    status: string;
    agent_name: string;
    version?: number;
    suite_id: string;
    total_tests: number;
    completed_tests: number;
    failed_tests: number;
    elapsed_time: number;
    created_at: string;
    completed_at?: string;
    cost_estimate_usd: string;
  }> =>
    awsApi.get(`/prompt-eval/batch?batchId=${encodeURIComponent(batchId)}`),

  /** List batch eval runs, optionally filtered by agent_name */
  listBatches: (agentName?: string): Promise<{ batches: Array<{
    batch_id: string;
    status: string;
    agent_name: string;
    version?: number;
    suite_id: string;
    total_tests: number;
    completed_tests?: number;
    failed_tests?: number;
    created_at: string;
    completed_at?: string;
    cost_estimate_usd?: string;
  }> }> => {
    const qp = new URLSearchParams();
    if (agentName) qp.append('agent_name', agentName);
    const qs = qp.toString();
    return awsApi.get(`/prompt-eval/batch${qs ? `?${qs}` : ''}`);
  },

  /**
   * Abort a running batch (sets status=ABORTED and purges the SQS queue).
   * POST /prompt-eval/batch/abort
   */
  abortBatch: (batchId: string): Promise<{
    batch_id: string;
    status: 'ABORTED';
    message: string;
  }> =>
    awsApi.post('/prompt-eval/batch/abort', { batch_id: batchId }),
};

// Pipeline Runs API (Admin Only)
export const awsPipelineRunsApi = {
  /**
   * List pipeline runs with pagination
   * @param params - limit, lastEvaluatedKey for pagination
   */
  list: (params?: { limit?: number; lastEvaluatedKey?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return awsApi.get(`/pipeline-runs${query}`);
  },

  /**
   * Get run details with presigned asset URLs
   * @param runId - Pipeline run UUID
   */
  get: (runId: string) =>
    awsApi.get(`/pipeline-runs?runId=${encodeURIComponent(runId)}`),

  /**
   * Create a new pipeline run (queues for execution)
   * @param params - kuralNumber, languages, traditions, llmConfig
   */
  create: (params: CreateVerseRunRequest) =>
    awsApi.post('/pipeline-runs', params),
};

// Verse (Master Orchestrator) pipeline types (Issue #1525)
export interface CreateVerseRunRequest {
  kuralNumber: number;
  languages?: string[];
  traditions?: string[];
  llmConfig?: {
    provider?: string;
    model?: string;
    narratorOnly?: boolean;
    skipCharacterConsistency?: boolean;
    imageStyleStrategy?: string;
  };
}

// Step Functions Browser API (Admin Only)
export const awsStepFunctionsApi = {
  /**
   * List all state machines in the account
   * @returns { stateMachines: Array<{ name, arn, type, creationDate }> }
   */
  listStateMachines: () =>
    awsApi.get('/step-functions'),

  /**
   * List executions for a state machine (paginated)
   * @param params.stateMachineArn - ARN of the state machine
   * @param params.statusFilter - Optional: RUNNING | SUCCEEDED | FAILED | TIMED_OUT | ABORTED
   * @param params.nextToken - Optional: pagination token
   * @param params.maxResults - Optional: 1-100, default 20
   * @returns { executions: Array<{ name, arn, status, startDate, stopDate }>, nextToken? }
   */
  listExecutions: (params: {
    stateMachineArn: string;
    statusFilter?: string;
    nextToken?: string;
    maxResults?: number;
  }) => {
    const qp = new URLSearchParams();
    qp.append('stateMachineArn', params.stateMachineArn);
    if (params.statusFilter) qp.append('statusFilter', params.statusFilter);
    if (params.nextToken) qp.append('nextToken', params.nextToken);
    if (params.maxResults) qp.append('maxResults', params.maxResults.toString());
    return awsApi.get(`/step-functions?${qp.toString()}`);
  },

  /**
   * Get execution detail with presigned S3 URLs in input/output
   * @param executionArn - Full execution ARN
   * @returns { name, arn, stateMachineArn, status, startDate, stopDate, input, output, error, cause }
   */
  describeExecution: (executionArn: string) =>
    awsApi.get(`/step-functions?executionArn=${encodeURIComponent(executionArn)}`),

  /**
   * Get execution history summary (state transitions without full data).
   * Returns filtered events: StateEntered, StateExited, StateFailed, Execution*.
   * Paginated via nextToken.
   * @param executionArn - Full execution ARN
   * @param nextToken - Optional pagination token
   * @returns { events: Array<{ id, type, timestamp, previousEventId, stateName }>, nextToken? }
   */
  getExecutionHistory: (executionArn: string, nextToken?: string) => {
    const qp = new URLSearchParams();
    qp.append('executionArn', executionArn);
    qp.append('history', 'true');
    if (nextToken) qp.append('nextToken', nextToken);
    return awsApi.get(`/step-functions?${qp.toString()}`);
  },

  /**
   * Get full detail for a single execution history event, including
   * input/output data enriched with presigned S3 URLs.
   * @param executionArn - Full execution ARN
   * @param eventId - Numeric event ID from the history summary
   * @returns { id, type, timestamp, stateName, stateInput?, stateOutput?, error?, cause? }
   */
  getEventDetail: (executionArn: string, eventId: number) =>
    awsApi.get(`/step-functions?executionArn=${encodeURIComponent(executionArn)}&eventId=${eventId}`),

  /**
   * List all media assets (images, audio, video) for a pipeline run.
   * @param runId - The pipeline run ID (S3 prefix)
   * @returns { runId, media: { video: [...], audio: [...], images: [...] }, counts }
   */
  getExecutionMedia: (runId: string) =>
    awsApi.get(`/step-functions?media=true&runId=${encodeURIComponent(runId)}`),

  /**
   * Rerun an execution: start a new execution with the same input on the same state machine.
   * @param params.stateMachineArn - ARN of the state machine
   * @param params.input - The original execution input payload
   * @param params.originalExecutionName - Name of the original execution (used to derive new name)
   * @returns { executionArn, name, startDate }
   */
  rerunExecution: (params: {
    stateMachineArn: string;
    input: any;
    originalExecutionName: string;
  }): Promise<{ executionArn: string; name: string; startDate: string }> =>
    awsApi.post('/step-functions', params),

  /**
   * Get pipeline traces for a run (used by rendered prompt viewer).
   * Calls the /pipeline-traces backend endpoint.
   * @param runId - Pipeline run UUID
   * @param lang - Optional language filter
   * @returns { runId, traces: Array<{ agent, language, iteration, prompt_rendered_url, ... }> }
   */
  getTraces: (runId: string, lang?: string) => {
    const qp = new URLSearchParams({ runId });
    if (lang) qp.append('lang', lang);
    return awsApi.get(`/pipeline-traces?${qp.toString()}`);
  },
};

// Micro Drama Launch Pad API (Admin Only) - Issue #1321
// Campaign creation, approval gate management, and artifact viewing

export interface MicroDramaCampaign {
  brandName: string;
  productName: string;
  productDescription: string;
  productFeatures?: string[];
  targetAudience?: {
    ageRange?: string;
    interests?: string[];
    demographics?: string;
  };
  brandTone?: string;
  productIntegration?: string;
  market?: string;
  genre?: string;
  storyDirection?: {
    oneLiner?: string;
    emotionalTone?: string[];
    keyMessage?: string;
    setting?: string;
    protagonist?: string;
  };
  lockedMoments?: {
    id: string;
    title: string;
    description: string;
    whyItMatters: string;
    positionHint: string;
  }[];
}

export interface CreateMicroDramaRunRequest {
  campaign: MicroDramaCampaign;
  llmConfig?: {
    provider?: string;
    model?: string;
    narratorOnly?: boolean;
    skipCharacterConsistency?: boolean;
    imageStyleStrategy?: string;
  };
  videoConfig?: { provider: string; model: string; aspectRatio: string };
  gates?: { skipPlotGate: boolean; skipVideoGate: boolean; skipTreatmentGate?: boolean };
}

export interface MicroDramaRun {
  runId: string;
  executionArn: string;
  executionName: string;
  startDate: string;
  status: string;
  createdAt: string;
}

export interface PendingApproval {
  executionId: string;
  executionName: string;
  gateName: 'treatment' | 'plot' | 'video_script';
  runId: string;
  status: string;
  createdAt: string;
  ttl: number;
  expiresAt: string;
}

export interface MicroDramaArtifacts {
  runId: string;
  artifacts: {
    genre?: any;
    treatment?: any;
    plot?: any;
    stories?: any;
    evaluations?: any;
    visualMomentPlan?: any;
    dialogues?: any;
    enrichedStory?: any;
    format_hero_visualMomentPlan?: any;
    format_hero_dialogues?: any;
    format_60s_visualMomentPlan?: any;
    format_60s_dialogues?: any;
    format_30s_visualMomentPlan?: any;
    format_30s_dialogues?: any;
    format_15s_visualMomentPlan?: any;
    format_15s_dialogues?: any;
  };
  presignedUrls: Record<string, string>;
}

export const awsMicroDramaApi = {
  /**
   * Create a new micro drama campaign run
   * Generates a UUID, stores campaign brief, and starts the Step Function
   */
  createRun: (data: CreateMicroDramaRunRequest): Promise<MicroDramaRun> =>
    awsApi.post('/micro-drama/runs', data),

  /**
   * Retry a failed micro drama run with the same campaign data
   * @param runId - Original run UUID to retry
   */
  retryRun: (runId: string): Promise<MicroDramaRun> =>
    awsApi.post(`/micro-drama/runs/${runId}/retry`, {}),

  /**
   * List pending approval gates
   * @param status - Filter by status (default: PENDING)
   * @param gateName - Filter by gate name (plot, video_script)
   */
  listApprovals: (status?: string, gateName?: string): Promise<{ approvals: PendingApproval[]; count: number }> => {
    const qp = new URLSearchParams();
    if (status) qp.append('status', status);
    if (gateName) qp.append('gateName', gateName);
    const query = qp.toString() ? `?${qp.toString()}` : '';
    return awsApi.get(`/micro-drama/approvals${query}`);
  },

  /**
   * Approve a gate (sends task success to Step Functions)
   * @param executionId - Step Function execution ARN
   * @param gateName - Gate name (plot, video_script)
   * @param reviewer - Reviewer name
   * @param editedArtifacts - Optional edited artifacts to write to S3 before approving
   */
  approve: (
    executionId: string,
    gateName: string,
    reviewer: string,
    editedArtifacts?: Record<string, any>,
  ): Promise<{ success: boolean; action: string; executionId: string; gateName: string }> =>
    awsApi.post(`/micro-drama/approvals/${encodeURIComponent(executionId)}/${gateName}`, {
      action: 'approve',
      reviewer,
      editedArtifacts,
    }),

  /**
   * Reject a gate (sends task failure to Step Functions)
   * @param executionId - Step Function execution ARN
   * @param gateName - Gate name (plot, video_script)
   * @param reviewer - Reviewer name
   * @param reason - Optional rejection reason
   */
  reject: (
    executionId: string,
    gateName: string,
    reviewer: string,
    reason?: string,
  ): Promise<{ success: boolean; action: string; executionId: string; gateName: string }> =>
    awsApi.post(`/micro-drama/approvals/${encodeURIComponent(executionId)}/${gateName}`, {
      action: 'reject',
      reviewer,
      reason,
    }),

  /**
   * Get S3 artifacts for a micro drama run
   * @param runId - Pipeline run UUID
   */
  getArtifacts: (runId: string): Promise<MicroDramaArtifacts> =>
    awsApi.get(`/micro-drama/artifacts/${encodeURIComponent(runId)}`),
};

// ==================== NEWS EXPLAINER LAUNCH PAD (Issue #1525) ====================

export interface NewsExplainerTopic {
  title: string;
  sector: string;
  sourceUrls?: string[];
  angle?: string;
  targetDurationMinutes?: number;
  genre?: string;
}

export interface CreateNewsExplainerRunRequest {
  topic: NewsExplainerTopic;
  /** NewsData.io image search language. Hard-required by the API. */
  language: string;
  llmConfig?: {
    provider?: string;
    model?: string;
    skipNewsImages?: boolean;
    imageStyleStrategy?: string;
  };
  targetLanguages?: string[];
  formats?: string[];
  gates?: { skipResearchGate: boolean; skipScriptGate: boolean };
  userMediaKeys?: string[];
}

export interface NewsExplainerRun {
  runId: string;
  executionArn: string;
  executionName: string;
  startDate: string;
  status: string;
  createdAt: string;
}

export const awsNewsExplainerApi = {
  /**
   * Create a new news explainer run
   * Generates a UUID, stores topic, and starts the Step Function
   */
  createRun: (data: CreateNewsExplainerRunRequest): Promise<NewsExplainerRun> =>
    awsApi.post('/news-explainer/runs', data),

  /**
   * List pending approval gates for news explainer pipeline
   * @param status - Filter by status (default: PENDING)
   * @param gateName - Filter by gate name (research, script)
   */
  listApprovals: (status?: string, gateName?: string): Promise<{ approvals: PendingApproval[]; count: number }> => {
    const qp = new URLSearchParams();
    if (status) qp.append('status', status);
    if (gateName) qp.append('gateName', gateName);
    const query = qp.toString() ? `?${qp.toString()}` : '';
    return awsApi.get(`/news-explainer/approvals${query}`);
  },

  /**
   * Approve a gate (sends task success to Step Functions)
   */
  approve: (
    executionId: string,
    gateName: string,
    reviewer: string,
    editedArtifacts?: Record<string, any>,
  ): Promise<{ success: boolean; action: string; executionId: string; gateName: string }> =>
    awsApi.post(`/news-explainer/approvals/${encodeURIComponent(executionId)}/${gateName}`, {
      action: 'approve',
      reviewer,
      editedArtifacts,
    }),

  /**
   * Reject a gate (sends task failure to Step Functions)
   */
  reject: (
    executionId: string,
    gateName: string,
    reviewer: string,
    reason?: string,
  ): Promise<{ success: boolean; action: string; executionId: string; gateName: string }> =>
    awsApi.post(`/news-explainer/approvals/${encodeURIComponent(executionId)}/${gateName}`, {
      action: 'reject',
      reviewer,
      reason,
    }),
};

// ==================== STORYBOARD INTERVIEW API (Issue #1982) ====================

export interface InterviewStartRequest {
  genre?: string;
}

export interface InterviewStartResponse {
  sessionId: string;
  message: string;
  done: boolean;
}

export interface InterviewMessageRequest {
  sessionId: string;
  message: string;
}

export interface InterviewSummary {
  topic_hint?: string;
  base_genre?: string;
  tone?: string;
  audience_level?: string;
  target_duration?: string;
  visual_style?: string;
  music_mood?: string;
  chart_preferences?: string;
  compliance_needs?: string;
  additional_notes?: string;
}

export interface InterviewMessageResponse {
  message: string;
  done: boolean;
  /** Number of user+assistant exchange pairs written so far */
  exchangeCount?: number;
  summary?: InterviewSummary;
}

export interface InterviewFinalizeRequest {
  sessionId: string;
}

export interface InterviewConfigData {
  [key: string]: string | number | boolean | Record<string, unknown>;
}

export interface InterviewFinalizeResponse {
  configId: string;
  configName: string;
  config: InterviewConfigData;
  alreadyFinalized?: boolean;
}

export interface InterviewConfigPreview {
  tone?: string;
  narrative_style?: string;
  music_mood?: string;
  visual_style?: string;
  chart_style?: string;
  genre?: string;
}

export interface SavedInterviewConfig {
  configId: string;
  configName: string;
  genre: string;
  createdAt: string;
  description?: string;
  configPreview: InterviewConfigPreview;
}

export interface InterviewListConfigsResponse {
  configs: SavedInterviewConfig[];
}

export interface InterviewStatusResponse {
  /** "ACTIVE" | "COMPLETED" */
  status: string;
  /** Number of user+assistant exchange pairs so far */
  exchangeCount: number;
  /** Most recent assistant message, or null if none yet */
  latestAssistantMessage: string | null;
  /** true when status === "COMPLETED" */
  done: boolean;
}

const interviewApi = INTERVIEW_API_BASE_URL
  ? new AwsApiClient(INTERVIEW_API_BASE_URL)
  : awsApi;

export const awsInterviewApi = {
  /** Start a new interview session */
  start: (data?: InterviewStartRequest): Promise<InterviewStartResponse> =>
    interviewApi.post('/interview/start', data ?? {}),

  /** Send a message in the interview conversation */
  sendMessage: (data: InterviewMessageRequest): Promise<InterviewMessageResponse> =>
    interviewApi.post('/interview/message', data),

  /** Finalize the interview and generate a config */
  finalize: (data: InterviewFinalizeRequest): Promise<InterviewFinalizeResponse> =>
    interviewApi.post('/interview/finalize', data),

  /** List saved interview configs */
  listConfigs: (): Promise<InterviewListConfigsResponse> =>
    interviewApi.get('/interview/configs'),

  /**
   * Poll session state — lightweight read-only endpoint.
   * Used as a fallback when POST /message returns 503 (API GW 29s timeout).
   * The action group Lambda continues running and writes its result to DynamoDB;
   * the frontend polls here every 3s until exchangeCount increments or done=true.
   */
  getStatus: (sessionId: string): Promise<InterviewStatusResponse> =>
    interviewApi.get(`/interview/session/${encodeURIComponent(sessionId)}/status`),
};

// User Media API — presigned URL upload for user-supplied media files (news explainer)
export const awsUserMediaApi = {
  /**
   * Get a presigned S3 upload URL for a user media file.
   * Backend: POST /news-explainer/runs/{runId}/user-media/presign
   * Body: { filename, contentType }
   * Response: { uploadUrl, s3Key, maxSizeBytes }
   */
  getUploadUrl: (runId: string, data: { filename: string; contentType: string }): Promise<{ uploadUrl: string; s3Key: string; maxSizeBytes: number }> =>
    awsApi.post(`/news-explainer/runs/${encodeURIComponent(runId)}/user-media/presign`, data),
};


// AI Image Generation API
// AI endpoints are on a separate API Gateway (AiGenerationStack)
// Use NEXT_PUBLIC_AI_API_URL first, fall back to main API only if not set
const AI_API_BASE_URL = process.env.NEXT_PUBLIC_AI_API_URL || process.env.NEXT_PUBLIC_AWS_API_URL;

export interface AIImageGenerationRequest {
  prompt: string;
  negative_prompt?: string;
  width?: number;  // 256-2048, default 1024
  height?: number; // 256-2048, default 1024
  steps?: number;  // 1-4 for schnell, default 4
  seed?: number;   // For reproducibility
  stream_id?: string;
  quote_ids?: string[];  // Optional: auto-assign to quotes
  speed?: 'normal' | 'fast';  // Generation speed tier (UI selection)
  tier?: 'normal' | 'lightning';  // GPU tier: 'lightning' routes to H100 for faster generation

  // Phase 2: Style options (LoRA)
  style_preset?: string;  // 'none', 'cinematic', 'anime', etc.
  style_strength?: number;  // 0.0-1.0, default 0.8

  // Phase 2: Reference image (IP-Adapter)
  reference_image_id?: string;  // media_id of reference image
  reference_strength?: number;  // 0.0-1.0, default 0.7

  // Phase 2: Animation (AnimateDiff)
  animate?: boolean;  // Enable animation
  motion_preset?: string;  // 'subtle', 'nature', 'dramatic', etc.
  duration_seconds?: number;  // 2-5 seconds
  output_format?: 'gif' | 'mp4' | 'webm';  // Output format
}

// Response from job submission (async)
export interface AIImageJobSubmitResponse {
  job_id: string;
  status: 'queued';
  message: string;
  media_type?: 'image' | 'animated';
  style_preset?: string;
  animate?: boolean;
}

// Style preset definition
export interface StylePreset {
  id: string;
  name: string;
  file: string | null;
}

// Motion preset definition
export interface MotionPreset {
  id: string;
  name: string;
  description: string;
}

// Response from job status polling
export interface AIImageJobStatusResponse {
  job_id: string;
  media_id?: string;  // ID of the generated media record (for approve/delete operations)
  status: 'queued' | 'processing' | 'completed' | 'failed';
  prompt?: string;
  width?: number;
  height?: number;
  steps?: number;
  stream_id?: string;
  user_id?: string;
  s3_key?: string;
  s3_bucket?: string;
  image_url?: string;  // Presigned URL (if completed)
  generation_time?: number;
  error?: string;  // Error message if failed
  created_at?: string;
  completed_at?: string;
  updated_at?: string;
}

// Legacy sync response (for backwards compatibility)
export interface AIImageGenerationResponse {
  image_id: string;
  image_url: string;
  s3_key: string;
  s3_bucket: string;
  prompt: string;
  negative_prompt: string;
  generation_time: number;
  parameters: {
    width: number;
    height: number;
    steps: number;
    seed: number;
    model: string;
  };
  user_id: string;
  stream_id: string;
  created_at: string;
}

class AiApiClient {
  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    if (!AI_API_BASE_URL) {
      throw new Error('AI API not configured - NEXT_PUBLIC_AI_API_URL environment variable is required');
    }

    // Get the session from NextAuth for auth token
    const session = await getSession() as any;
    if (!session?.user?.email) {
      throw new Error('Not authenticated - please sign in');
    }

    const idToken = session.idToken;
    const url = `${AI_API_BASE_URL}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    // Include auth token for future JWT validation
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await fetch(url, {
      headers,
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

      // Handle insufficient credits (402 Payment Required)
      if (response.status === 402 && errorData.error === 'insufficient_credits') {
        throw new InsufficientCreditsError(
          errorData.cost || 0,
          errorData.available || 0,
          errorData.message
        );
      }

      throw new Error(errorData.error || `AI API error: ${response.status}`);
    }

    return response.json();
  }

  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

const aiApi = new AiApiClient();

// Polling helper for async job status
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export interface PollOptions {
  maxAttempts?: number;
  intervalMs?: number;
  onStatusChange?: (status: AIImageJobStatusResponse) => void;
}

// Admin Credits API
export const awsAdminCreditsApi = {
  /**
   * Look up user by email
   * @param email - User's email address
   * @returns User information with credit balance
   */
  lookup: (email: string) =>
    awsApi.post('/admin/credits', { action: 'lookup', user_email: email }),

  /**
   * Add credits to user account
   * @param email - User's email address
   * @param amount - Number of credits to add (max 1000)
   * @param reason - Optional reason (required if amount > 100)
   * @returns Credit addition result
   */
  add: (email: string, amount: number, reason?: string) =>
    awsApi.post('/admin/credits', { action: 'add', user_email: email, amount, reason }),

  /**
   * Get credit history for user
   * @param email - User's email address
   * @returns List of credit changes
   */
  history: (email: string) =>
    awsApi.post('/admin/credits', { action: 'history', user_email: email }),
};

// AI Image Generation API (Async Pattern)
export const awsAiImagesApi = {
  /**
   * Submit an AI image generation job (async - returns immediately)
   * @param data - Generation parameters including prompt, dimensions, steps
   * @returns Job ID for status polling
   */
  submit: (data: AIImageGenerationRequest): Promise<AIImageJobSubmitResponse> =>
    aiApi.post('/ai-jobs', data),

  /**
   * Get the status of an AI image generation job
   * @param jobId - The job ID returned from submit()
   * @returns Current job status and image URL when completed
   */
  getStatus: (jobId: string): Promise<AIImageJobStatusResponse> =>
    aiApi.get(`/ai-jobs/${jobId}`),

  /**
   * Delete an AI generated image (hard delete from S3 + DynamoDB)
   * @param mediaId - The media ID to delete
   * @returns Success confirmation
   */
  delete: (mediaId: string): Promise<{ message: string; media_id: string }> =>
    aiApi.post('/ai-media/delete', { media_id: mediaId }),

  /**
   * Approve an AI image - copy to stream gallery
   * @param mediaId - The media ID to approve
   * @param streamId - The stream to add the image to
   * @returns Success confirmation with new image details
   */
  approve: (mediaId: string, streamId: string): Promise<{ message: string; image_id: string; image_url: string }> =>
    aiApi.post('/ai-media/approve', { media_id: mediaId, stream_id: streamId }),

  /**
   * Poll for job completion with optional callbacks
   * @param jobId - The job ID to poll
   * @param options - Polling options (maxAttempts, intervalMs, onStatusChange)
   * @returns Final job status (completed or failed)
   */
  pollUntilComplete: async (
    jobId: string,
    options: PollOptions = {}
  ): Promise<AIImageJobStatusResponse> => {
    const {
      maxAttempts = 60,  // 2 minutes with 2s interval
      intervalMs = 2000,
      onStatusChange,
    } = options;

    let lastStatus: string | undefined;

    for (let i = 0; i < maxAttempts; i++) {
      const status = await awsAiImagesApi.getStatus(jobId);

      // Notify on status change
      if (onStatusChange && status.status !== lastStatus) {
        onStatusChange(status);
        lastStatus = status.status;
      }

      // Return when terminal state reached
      if (status.status === 'completed' || status.status === 'failed') {
        return status;
      }

      // Wait before next poll
      await sleep(intervalMs);
    }

    throw new Error(`Polling timeout: Job ${jobId} did not complete within ${maxAttempts * intervalMs / 1000} seconds`);
  },

  /**
   * Generate an AI image (convenience method that submits and polls)
   * @param data - Generation parameters
   * @param options - Polling options
   * @returns Completed job with image URL
   */
  generate: async (
    data: AIImageGenerationRequest,
    options?: PollOptions
  ): Promise<AIImageJobStatusResponse> => {
    const { job_id } = await awsAiImagesApi.submit(data);
    return awsAiImagesApi.pollUntilComplete(job_id, options);
  },

  /**
   * Check if the AI generation service is healthy
   */
  healthCheck: () => aiApi.get('/health'),

  /**
   * Check if AI API is configured
   */
  isConfigured: () => !!AI_API_BASE_URL,

  /**
   * Get available style presets (LoRA styles)
   * Returns both g5 styles and H100-exclusive styles
   */
  getStyles: (): Promise<{ styles: StylePreset[]; h100Styles: StylePreset[]; count: number }> =>
    aiApi.get('/ai-styles'),

  /**
   * Get available motion presets (AnimateDiff)
   */
  getMotions: (): Promise<{ motions: MotionPreset[]; count: number }> =>
    aiApi.get('/ai-motions'),

  /**
   * List recent AI image generations for a stream (Issue #835)
   * @param params - Filter by stream_id, status, limit
   * @returns List of generated media items with CDN URLs
   */
  listRecent: (params: {
    stream_id: string;
    status?: string;
    limit?: number;
  }): Promise<{
    items: Array<{
      media_id: string;
      media_url: string;
      prompt?: string;
      width?: number;
      height?: number;
      status: string;
      created_at: string;
      generation_time?: number;
      s3_key?: string;
    }>;
    count: number;
    has_more: boolean;
  }> => {
    const queryParams = new URLSearchParams();
    queryParams.append('stream_id', params.stream_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    return aiApi.get(`/ai-media?${queryParams.toString()}`);
  },

  /**
   * Generate image prompt from quote using LLM (Claude)
   * @param quote - Quote text to transform into image prompt
   * @returns AI-generated image prompt optimized for FLUX
   */
  generatePromptFromQuote: async (quote: string): Promise<{ prompt: string; quote: string }> => {
    return aiApi.post('/ai-prompt', { quote });
  },
};

// ==================== CHARACTER GALLERY API (PuLID) ====================

import type {
  Character,
  CreateCharacterRequest,
  UpdateCharacterRequest,
  CharacterListResponse,
} from '@/types/models';

/**
 * Character Gallery API for PuLID face consistency.
 * Uses the AI API Gateway (same as AI image generation).
 */
export const awsCharactersApi = {
  /**
   * List characters for a user or stream
   * @param params - Filter by user_id or stream_id
   */
  list: (params?: { user_id?: string; stream_id?: string; limit?: number }): Promise<CharacterListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.user_id) queryParams.append('user_id', params.user_id);
    if (params?.stream_id) queryParams.append('stream_id', params.stream_id);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return aiApi.get(`/ai-characters${query}`);
  },

  /**
   * Get a single character by ID
   * @param characterId - Character ID
   */
  get: (characterId: string): Promise<Character> =>
    aiApi.get(`/ai-characters/${characterId}`),

  /**
   * Create a new character with face image
   * @param data - Character creation data including base64 face image
   */
  create: (data: CreateCharacterRequest): Promise<Character> =>
    aiApi.post('/ai-characters', data),

  /**
   * Update character metadata
   * @param characterId - Character ID
   * @param data - Fields to update (name, description)
   */
  update: (characterId: string, data: UpdateCharacterRequest): Promise<Character> =>
    aiApi.patch(`/ai-characters/${characterId}`, data),

  /**
   * Delete a character and its associated images
   * @param characterId - Character ID
   */
  delete: (characterId: string): Promise<{ message: string; character_id: string }> =>
    aiApi.delete(`/ai-characters/${characterId}`),
};

// ==================== KNOWLEDGE BASE API ====================

export interface KBDocument {
  docId: string;
  streamId: string;
  userId: string;
  docType: 'file' | 'url' | 'text';
  fileName: string;
  fileType?: string;
  fileSize?: number;
  status: 'pending' | 'processing' | 'chunked' | 'ready' | 'failed';
  chunkCount?: number;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface KBUploadResponse {
  docId: string;
  uploadUrl: string;
}

export interface KBListResponse {
  documents: KBDocument[];
  count: number;
}

/**
 * Knowledge Base API for document upload and management
 */
export const awsKnowledgeBaseApi = {
  /**
   * Upload a file - get presigned URL for S3 upload
   * @param streamId - Stream ID
   * @param data - File metadata (fileName, fileType, fileSize)
   */
  uploadFile: (streamId: string, data: {
    fileName: string;
    fileType: string;
    fileSize: number;
  }): Promise<KBUploadResponse> =>
    awsApi.post(`/knowledge-base/upload`, {
      streamId: streamId,
      fileName: data.fileName,
      fileType: data.fileType,
      fileSize: data.fileSize,
      docType: 'file',
    }),

  /**
   * Add a URL source
   * @param streamId - Stream ID
   * @param sourceUrl - URL to scrape
   */
  addUrl: (streamId: string, sourceUrl: string): Promise<{ docId: string }> =>
    awsApi.post(`/knowledge-base/url`, {
      streamId: streamId,
      content: sourceUrl,
      docType: 'url',
    }),

  /**
   * Add pasted text
   * @param streamId - Stream ID
   * @param data - Title and content
   */
  addText: (streamId: string, data: {
    title: string;
    content: string;
  }): Promise<{ docId: string }> =>
    awsApi.post(`/knowledge-base/text`, {
      streamId: streamId,
      fileName: data.title,
      content: data.content,
      docType: 'text',
    }),

  /**
   * List documents for a stream
   * @param streamId - Stream ID
   */
  list: (streamId: string): Promise<KBListResponse> => {
    if (!streamId) {
      throw new Error('streamId is required');
    }
    return awsApi.get(`/knowledge-base?streamId=${streamId}`);
  },

  /**
   * Delete a document
   * @param docId - Document ID
   */
  delete: (docId: string): Promise<{ message: string }> =>
    awsApi.delete(`/knowledge-base/${docId}`),
};

// Admin viewer API (Admin Only) — Config Inspector + Baseline panels
// Migrated from app/api/admin/* Next.js routes to Lambda (PR D #2189, PR J #2206).
import type { ConfigItem, BaselineItem } from '@/types/admin';

export const awsAdminApi = {
  /** List all ACTIVE CONFIG# items, grouped by agent_name. */
  listConfigItems: (): Promise<{ items: ConfigItem[]; count: number }> =>
    awsApi.get('/admin/config-items'),

  /** List all ACTIVE BASELINE# items, grouped by (target_agent, target_version). */
  listBaselines: (): Promise<{ items: BaselineItem[]; count: number }> =>
    awsApi.get('/admin/baselines'),
};